/**
 * Met au pluriel une phrase ou un mot en fonction du nombre
 * @param n {number} Nombre utilisé pour déterminer la pluralisation
 * @param cases {Array} Tableau de tableaux, chacun contenant une condition et une fonction d'exécution
 * @returns {*} La version plurielle de la phrase ou du mot
 */
export function pluralize(n, ...cases) {
    for (const [cond, exec] of cases) {
        if (cond(n)) {
            return exec(n);
        }
    }
}

//Pour différer l'appel à la prochaine itération de la boucle d'événements et ainsi disposer d'une valeur cohérente de document.activeElement.
const nextTick = requestAnimationFrame;
// Instances
const __multiselects_instances__ = new Set();

/**
 * Représente un composant de contrôle à sélection multiple utilisé pour gérer
 * la sélection interactive de plusieurs éléments, avec des fonctionnalités telles que
 * le filtrage, la navigation au clavier et la mise à jour de l'interface utilisateur
 * en réponse aux interactions de l'utilisateur.
 */
export class MultiselectControl {
    #multiselect;
    #status;
    #summary;
    #itemsWrapper;
    #items;
    #filterInput;
    #popover;
    #loadingStatus;
    #control;
    #checked;
    #pills;
    #pillTemplate;
    #previousValue;
    #serialized;
    #itemsWithBoundEvents = new Set;
    #firstItemWithBoundEvents = null;
    #blurEventHandler = this.blurEventHandler.bind(this);

    /**
     * Crée une nouvelle instance de MultiselectControl.
     * @param multiselect {HTMLElement} L'élément HTML représentant le contrôle de sélection multiple.
     */
    constructor(multiselect) {
        this.#multiselect = multiselect;
        this.#status = new Proxy({
            currentIndex: null
        }, {
            get: (target, prop, receiver) => {
                return Reflect.get(target, prop, receiver);
            },
            set: (target, prop, value, receiver) => {
                const result = Reflect.set(target, prop, value, receiver);
                this.updateUI();
                return result;
            }
        });
    }

    /**
     * Retourne un ensemble contenant toutes les instances initialisées de MultiselectControl.
     * @returns {Set<any>} Un ensemble contenant toutes les instances initialisées de MultiselectControl.
     */
    static get instances() {
        return __multiselects_instances__;
    }

    /**
     * Méthode d'accès (getter) permettant d'obtenir la propriété privée `#control`.
     * @returns {*} La valeur de la propriété privée `#control`.
     */
    get control() {
        return this.#control;
    }

    /**
     * Retourne la valeur sérialisée du contrôle de sélection multiple.
     * @returns {*} La valeur sérialisée du contrôle de sélection multiple. Les identifiants des éléments sont séparés par le caractère ";".
     */
    get value() {
        return this.#serialized.value;
    }

    /**
     * Définit la valeur du contrôle de sélection multiple à partir de la notation sérialisée.
     * Exemple de représentation formatée de la chaîne : "item_1;item_2;item_3"
     * Le ";" est utilisé comme séparateur.
     * @param value {string} La valeur à définir.
     */
    set value(value) {
        const identifiers = String(value).split(';');
        const selectedIdentifiers = this.singleSelect ? identifiers.slice(-1) : identifiers;
        for (const item of this.#items()) {
            item.querySelector('input').checked = selectedIdentifiers.includes(item.querySelector('input').value);
        }
        this.updateUI();
    }

    /**
     * Méthode d'accès (getter) permettant d'obtenir la propriété privée `#items`.
     * @returns {*} La valeur de la propriété privée `#items`.
     */
    get items() {
        return this.#items();
    }

    /**
     * Méthode d'accès (getter) permettant d'obtenir la propriété privée `#previousValue`.
     * @returns {*} La valeur de la propriété privée `#previousValue`.
     */
    get previousValue() {
        return this.#previousValue;
    }

    /**
     * Retourne la valeur de l'attribut disabled du contrôle de sélection multiple.
     * @returns {*} true si le contrôle est désactivé, false sinon.
     */
    get disabled() {
        return this.#summary.disabled;
    }

    /**
     * Définit la valeur de l'attribut disabled du contrôle de sélection multiple.
     * @param value true désactive le contrôle, false l'active.
     */
    set disabled(value) {
        this.#summary.disabled = value;
        this.updateUI();
    }

    /**
     * Indique si le contrôle de sélection multiple est requis ou non.
     * @returns {boolean} true si le contrôle est requis, false sinon.
     */
    get required() {
        return this.#multiselect.classList.contains('js-multiselect-required');
    }

    /**
     * Indique si le contrôle de sélection multiple permet uniquement une sélection d'un seul élément.
     * @returns {boolean} true si seulement un élément peut être sélectionné, false sinon.
     */
    get singleSelect() {
        return this.#multiselect.dataset.singleSelect === 'true';
    }

    /**
     * Définit si le contrôle de sélection multiple est requis ou non.
     * @param value true rend le contrôle obligatoire, false le rend facultatif.
     */
    set required(value) {
        this.#multiselect.classList.toggle('js-multiselect-required', value);
        this.updateUI();
    }

    /**
     * Obtient l'index de l'élément sélectionné actuellement.
     * @returns {*} L'index de l'élément sélectionné, ou null si aucun élément n'est sélectionné.
     */
    get currentIndex() {
        return this.#status.currentIndex;
    }

    /**
     * Sélectionne l'élément à l'index donné.
     * @param index {number} L'index de l'élément à sélectionner.
     */
    set currentIndex(index) {
        this.#status.currentIndex = index;
    }

    /**
     * Initialise le contrôle de sélection multiple en liant les écouteurs d'événements,
     * mettant à jour l'interface utilisateur et configurant l'état initial.
     */
    init() {
        this.#summary = this.#multiselect.querySelector('.js-multiselect-summary');
        this.#itemsWrapper = this.#multiselect.querySelector('.js-multiselect-items');
        this.#filterInput = this.#multiselect.querySelector('.js-multiselect-filter input');
        this.#popover = this.#multiselect.querySelector('.js-multiselect-popover');
        this.#loadingStatus = this.#multiselect.querySelector('.js-multiselect-items-loading-state');
        this.#control = this.#multiselect.querySelector('.js-multiselect-control');
        this.#items = () => Array.from(this.#multiselect.querySelectorAll('.js-multiselect-item')) ?? [];
        this.#checked = () => this.#items().filter(item => item.querySelector('input').checked);
        this.#pills = this.#multiselect.querySelector('.js-multiselect-pills');
        this.#serialized = this.#multiselect.querySelector('.js-multiselect-serialized');
        this.#pillTemplate = document.getElementById(this.#multiselect.dataset.multiselectPillTemplate).content;
        if (!(__multiselects_instances__.has(this))) {
            __multiselects_instances__.add(this);
            this.#bindEvents();
            this.#bindItemsEvents();
        }
        this.updateUI();
    }

    /**
     * Bascule l'état de l'élément courant.
     */
    #toggleCurrentIndex() {
        if (this.currentIndex === null) {
            return;
        }
        this.#items()[this.currentIndex].querySelector('input').click();
    }

    /**
     * Sélectionne le premier élément de la liste de suggestions qui n'est pas masqué.
     */
    #selectFirstItemInSuggestionList() {
        this.currentIndex = this.#items().findIndex(item => item.style.display !== 'none');
    }

    /**
     * Lie les écouteurs d'événements au contrôle de sélection multiple.
     */
    #bindEvents() {
        this.#summary.addEventListener('keydown', event => {
            switch (event.key) {
                case 'ArrowDown':
                case 'ArrowUp': {
                    this.#popover.showPopover?.();
                    break;
                }
            }
        });
        this.#filterInput.addEventListener('input', () => {
            this.#selectFirstItemInSuggestionList();
        });
        this.#filterInput.addEventListener('keydown', event => {
            switch (event.key) {
                case 'ArrowDown': {
                    event.preventDefault();
                    this.currentIndex = this.#findNextIndex();
                    break;
                }
                case 'ArrowUp': {
                    event.preventDefault();
                    this.currentIndex = this.#findPreviousIndex();
                    break;
                }
                case 'Enter': {
                    event.preventDefault();
                    this.#toggleCurrentIndex();
                    break;
                }
            }
        });
        this.#itemsWrapper.addEventListener('beforefilter', () => {
            this.#previousValue = this.#checked().map(item => item.querySelector('input').value);
            this.#loadingStatus.removeAttribute('hidden');
        });
        this.#itemsWrapper.addEventListener('afterfilter', () => {
            this.#loadingStatus.setAttribute('hidden', '');
            nextTick(() => {
                this.#bindItemsEvents();
                const previousValue = this.singleSelect ? this.previousValue.slice(-1) : this.previousValue;
                for (const item of this.items) {
                    item.querySelector('input').checked = previousValue.includes(item.querySelector('input').value);
                }
                this.#selectFirstItemInSuggestionList();
                this.updateUI();
            });
        });
        this.#popover.addEventListener('toggle', event => {
            switch (event.newState) {
                case 'open': {
                    this.#handlePopoverOpen();
                    break;
                }
                case 'closed': {
                    this.#handlePopoverClose();
                    break;
                }
            }
        });
    }

    /**
     * Associe les écouteurs d'événements aux éléments du contrôle de sélection multiple.
     */
    #bindItemsEvents() {
        const items = this.#items();
        for (const item of items) {
            if (this.#itemsWithBoundEvents.has(item)) {
                continue;
            }
            this.#itemsWithBoundEvents.add(item);
            item.querySelector('label').addEventListener('focus', () => {
                this.currentIndex = this.#items().indexOf(item);
            });
            item.querySelector('label').addEventListener('keydown', event => {
                switch (event.key) {
                    case 'Enter': {
                        event.preventDefault();
                        this.#toggleCurrentIndex();
                        break;
                    }
                    case 'ArrowDown': {
                        event.preventDefault();
                        this.currentIndex = this.#findNextIndex();
                        break;
                    }
                    case 'ArrowUp': {
                        event.preventDefault();
                        this.currentIndex = this.#findPreviousIndex();
                        break;
                    }
                }
            });
            item.querySelector('input').addEventListener('change', event => {
                this.#keepOnlySelectedInput(event.currentTarget);
                this.updateUI();
            });
            item.addEventListener('mouseenter', () => {
                this.currentIndex = this.#items().indexOf(item);
            });
            item.addEventListener('mousemove', () => {
                this.currentIndex = this.#items().indexOf(item);
            });
            item.addEventListener('mouseleave', () => {
                this.currentIndex = null;
            });
        }
        // La gestion des événements "invalid" et l'affichage des messages d'erreur sont externalisées dans frontoffice-saisie-cf.js
        const firstItem = items.find(item => item.style.display !== 'none');
        if (firstItem && firstItem !== this.#firstItemWithBoundEvents) {
            this.#firstItemWithBoundEvents = firstItem;
        }
    }

    /**
     * Décoche tous les éléments sauf l'entrée donnée lorsque le mode de sélection unique est activé.
     * @param selectedInput {HTMLInputElement} L'entrée qui doit rester cochée.
     */
    #keepOnlySelectedInput(selectedInput) {
        if (!this.singleSelect || !selectedInput.checked) {
            return;
        }
        for (const item of this.#items()) {
            const input = item.querySelector('input');
            if (input !== selectedInput) {
                input.checked = false;
            }
        }
    }

    /**
     * Gère l'ouverture du popover.
     */
    #handlePopoverOpen() {
        this.#filterInput.dispatchEvent(new CustomEvent('applyfilter'));
        this.#filterInput.focus();
        this.currentIndex = 0;
        this.#summary.addEventListener('blur', this.#blurEventHandler);
        this.#filterInput.addEventListener('blur', this.#blurEventHandler);
        for (const item of this.#items()) {
            item.querySelector('input').addEventListener('blur', this.#blurEventHandler);
            item.querySelector('label').addEventListener('blur', this.#blurEventHandler);
        }
    }

    /**
     * Gère la fermeture du popover.
     */
    #handlePopoverClose() {
        this.#summary.removeEventListener('blur', this.#blurEventHandler);
        this.#filterInput.removeEventListener('blur', this.#blurEventHandler);
        for (const item of this.#items()) {
            item.querySelector('input').removeEventListener('blur', this.#blurEventHandler);
            item.querySelector('label').removeEventListener('blur', this.#blurEventHandler);
        }
    }

    /**
     * Trouve l'index de l'élément précédent dans la liste de suggestions.
     * @returns {*|null} L'index de l'élément précédent, ou null si aucun élément précédent n'est trouvé.
     */
    #findPreviousIndex() {
        const items = this.#items();
        const displayedItemsIndexes = items.filter(item => item.style.display !== 'none').map(item => items.indexOf(item));
        const firstItemIndex = displayedItemsIndexes.at(0);
        const lastItemIndex = displayedItemsIndexes.at(-1);
        if (items.length === 0) {
            return null;
        }
        if (this.currentIndex === null || this.currentIndex <= firstItemIndex) {
            return lastItemIndex;
        }
        return displayedItemsIndexes.findLast(index => index < this.currentIndex);
    }

    /**
     * Trouve l'index de l'élément suivant dans la liste de suggestions.
     * @returns {*|null} L'index de l'élément suivant, ou null si aucun élément suivant n'est trouvé.
     */
    #findNextIndex() {
        const items = this.#items();
        const displayedItemsIndexes = items.filter(item => item.style.display !== 'none').map(item => items.indexOf(item));
        const firstItemIndex = displayedItemsIndexes.at(0);
        const lastItemIndex = displayedItemsIndexes.at(-1);
        if (items.length === 0) {
            return null;
        }
        if (this.currentIndex === null || this.currentIndex >= lastItemIndex) {
            return firstItemIndex;
        }
        return displayedItemsIndexes.find(index => index > this.currentIndex);
    }

    /**
     * Gère l'événement blur du contrôle de sélection multiple.
     */
    blurEventHandler() {
        nextTick(() => {
            if (this.control.contains(document.activeElement)) {
                return;
            }
            this.#popover?.hidePopover?.();
        });
    }

    /**
     * Met à jour l'interface utilisateur du contrôle de sélection multiple.
     * Cette méthode met à jour le résumé, les éléments sélectionnés et les pastilles.
     * Elle garantit également que l'élément courant est mis en surbrillance et défilé dans la zone visible.
     */
    updateUI() {
        const items = this.#items();
        const checked = this.#checked();
        this.#serialized.value = checked.map(item => item.querySelector('input').value).join(';');
        this.#summary.textContent = pluralize(
            checked.length,
            [n => n === 0, () => 'Sélectionnez un ou plusieurs éléments'],
            [n => n === 1, () => '1 élément sélectionné'],
            [n => n > 1, n => n + ' éléments sélectionnés']
        );
        for (const item of items) {
            item.querySelector('input').required = this.required && checked.length === 0;
        }
        const previousItem = items.find(item => item.classList.contains('form-field__select-item--current'));
        const previousItemIndex = items.indexOf(previousItem);
        if (previousItemIndex !== null && previousItemIndex !== this.currentIndex) {
            items.find(item => item.classList.contains('form-field__select-item--current'))?.classList.remove('form-field__select-item--current');
            if (this.currentIndex !== null) {
                items[this.currentIndex]?.classList.add('form-field__select-item--current');
                items[this.currentIndex]?.scrollIntoView({block: 'nearest', behavior: 'smooth'});
            }
        }
        this.#pills.innerHTML = '';
        for (const item of checked) {
            const template = this.#pillTemplate.cloneNode(true);
            const removePill = () => {
                item.querySelector('input').checked = false;
                this.updateUI();
            };
            template.querySelector('.js-multiselect-pill-label').textContent = item.querySelector('label').textContent;
            template.querySelector('.js-multiselect-pill-remove').addEventListener('click', removePill);
            template.querySelector('.js-multiselect-pill-remove').addEventListener('keydown', event => {
                switch (event.key) {
                    case 'Backspace':
                    case 'Delete': {
                        event.preventDefault();
                        removePill();
                        break;
                    }
                }
            });
            this.#pills.appendChild(template);
        }
    }
}